// 거래: the month's rows by day (newest first), the month's sums, and the
// sheet that changes a row. A PC shows a wide table, a phone a list.
import { useMemo, useState } from 'react'

import { Spinner } from '../../ui/common'
import { useMedia } from '../../ui/router'
import { dayLabel, won } from '../format'
import { type ShownRow, sortRows } from '../model'
import { categoryPath, itemName, rowsOfMonth, totals } from '../views'
import { type Current, Money, MonthSwitch } from './common'
import { QuickEntry, RowSheet } from './Entry'

function PendingTag({ row }: { row: ShownRow }) {
  if (!row.pending) return null
  return <span className="tag t-pending">{row.pending === 'waiting' ? '연결 기다림' : '보내는 중'}</span>
}

export function MonthSums({ rows }: { rows: ShownRow[] }) {
  const t = totals(rows)
  return (
    <dl className="t-sums">
      <div>
        <dt>수입</dt>
        <dd className="income">{won(t.income)}</dd>
      </div>
      <div>
        <dt>지출</dt>
        <dd>{won(t.expense)}</dd>
      </div>
      <div>
        <dt>남은 돈</dt>
        <dd className={t.balance < 0 ? 'danger' : ''}>{won(t.balance)}</dd>
      </div>
    </dl>
  )
}

/** One row in a list (phone, the summary's latest). */
export function RowLine({ cur, row, onOpen }: { cur: Current; row: ShownRow; onOpen: (r: ShownRow) => void }) {
  const items = cur.book?.items ?? {}
  const meta = [itemName(items, row.person_item), itemName(items, row.payment_item)].filter((s) => s !== '(없음)')
  return (
    <li>
      <button type="button" className="t-row" onClick={() => onOpen(row)}>
        <span className="grow">
          <span className="t-row-title">
            {categoryPath(items, row.category_item)}
            <PendingTag row={row} />
          </span>
          {(row.memo || meta.length > 0) && (
            <span className="t-row-meta">{[row.memo, ...meta].filter(Boolean).join(' · ')}</span>
          )}
        </span>
        <Money amount={row.amount} kind={row.kind} />
      </button>
    </li>
  )
}

export function RowsScreen({ cur, month, setMonth }: { cur: Current; month: string; setMonth: (m: string) => void }) {
  const wide = useMedia('(min-width: 900px)')
  const [editing, setEditing] = useState<ShownRow | null>(null)
  const [adding, setAdding] = useState(false)
  const rows = useMemo(() => sortRows(rowsOfMonth(cur.rows, month)), [cur.rows, month])
  const days = useMemo(() => {
    const out: { day: string; rows: ShownRow[] }[] = []
    for (const r of rows) {
      const last = out[out.length - 1]
      if (last && last.day === r.day) last.rows.push(r)
      else out.push({ day: r.day, rows: [r] })
    }
    return out
  }, [rows])
  const items = cur.book?.items ?? {}
  // the sheet follows the row while it is open (a change from elsewhere)
  const open = editing ? (cur.rows.find((r) => r.client_id === editing.client_id) ?? null) : null

  return (
    <div className="page t-page t-wide-page">
      <MonthSwitch month={month} onChange={setMonth} />
      <MonthSums rows={rows} />
      <button type="button" className="primary" onClick={() => setAdding(true)}>
        거래 추가
      </button>
      {rows.length === 0 &&
        (cur.book?.loading ? <Spinner /> : <p className="empty-note muted">이 달에 적은 거래가 없습니다.</p>)}
      {rows.length > 0 && wide && (
        <table className="t-table t-rows">
          <thead>
            <tr>
              <th>날짜</th>
              <th>구분</th>
              <th className="num">금액</th>
              <th>분류</th>
              <th>사람</th>
              <th>결제수단</th>
              <th>메모</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.client_id} onClick={() => setEditing(r)} className={r.pending ? 'pending' : ''}>
                <td>
                  <button type="button" className="t-cell-btn" onClick={() => setEditing(r)}>
                    {dayLabel(r.day)}
                  </button>
                </td>
                <td>{r.kind === 'income' ? '수입' : '지출'}</td>
                <td className="num">
                  <Money amount={r.amount} kind={r.kind} />
                </td>
                <td>
                  {categoryPath(items, r.category_item)}
                  <PendingTag row={r} />
                </td>
                <td>{itemName(items, r.person_item)}</td>
                <td>{itemName(items, r.payment_item)}</td>
                <td className="t-memo">{r.memo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length > 0 && !wide && (
        <div className="t-days">
          {days.map((d) => {
            const t = totals(d.rows)
            return (
              <section key={d.day} className="card t-day">
                <h3>
                  <span>{dayLabel(d.day)}</span>
                  <span className="muted small">
                    {t.income > 0 && `+${won(t.income)} `}
                    {t.expense > 0 && `-${won(t.expense)}`}
                  </span>
                </h3>
                <ul className="list">
                  {d.rows.map((r) => (
                    <RowLine key={r.client_id} cur={cur} row={r} onOpen={setEditing} />
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      )}
      <RowSheet cur={cur} row={open} onClose={() => setEditing(null)} />
      <QuickEntry cur={cur} open={adding} onClose={() => setAdding(false)} />
    </div>
  )
}
